/** 设计令牌：Web（AntD 5 主题）、RN（Paper 主题）、小程序（NutUI）共用同一份数值。 */
export type ThemeMode = 'light' | 'dark';

export interface HqPalette {
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryBg: string;
  /** 辅助色（正文次级信息、说明文字）。 */
  secondary: string;
  /** 点缀色（高亮数据、暗色大屏强调）。 */
  accent: string;
  success: string;
  warning: string;
  error: string;
  info: string;
  text: string;
  textSecondary: string;
  textDisabled: string;
  border: string;
  borderSecondary: string;
  bgLayout: string;
  bgContainer: string;
  bgElevated: string;
  mask: string;
}

export const defaultPalette: HqPalette = {
  primary: '#88157A',
  primaryHover: '#AF4FA3',
  primaryActive: '#6F0863',
  primaryBg: '#f7ecf6',
  secondary: '#666666',
  accent: '#E8D48B',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#88157A',
  text: '#1f2329',
  textSecondary: '#666666',
  textDisabled: '#a8b0ba',
  border: '#d9dee5',
  borderSecondary: '#eef1f5',
  bgLayout: '#f5f7fa',
  bgContainer: '#ffffff',
  bgElevated: '#ffffff',
  mask: 'rgba(0, 0, 0, 0.45)'
};

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32
} as const;

export const radius = {
  xs: 2,
  sm: 4,
  md: 6,
  lg: 10,
  pill: 999
} as const;

export const fontSize = {
  xs: 12,
  sm: 13,
  md: 14,
  lg: 16,
  xl: 20,
  xxl: 24,
  display: 32
} as const;

export const fontWeight = {
  regular: '400',
  medium: '500',
  semibold: '600'
} as const;

export const lineHeight = {
  tight: 1.25,
  base: 1.5,
  loose: 1.75
} as const;

export const motion = {
  fast: '0.1s',
  base: '0.2s',
  slow: '0.3s'
} as const;

export const zIndex = {
  dropdown: 1050,
  modal: 1000,
  drawer: 1000,
  toast: 1080,
  /** 大屏轮播与浮层。 */
  screenOverlay: 900
} as const;

export interface HqTheme {
  mode: ThemeMode;
  palette: HqPalette;
  spacing: typeof spacing;
  radius: typeof radius;
  fontSize: typeof fontSize;
  fontWeight: typeof fontWeight;
  lineHeight: typeof lineHeight;
  motion: typeof motion;
  zIndex: typeof zIndex;
}

export const defaultTheme: HqTheme = {
  mode: 'light',
  palette: defaultPalette,
  spacing,
  radius,
  fontSize,
  fontWeight,
  lineHeight,
  motion,
  zIndex
};

export interface ThemeOverrides {
  mode?: ThemeMode;
  palette?: Partial<HqPalette>;
}

/** 以默认主题为基线合并覆盖项（不做深拷贝以外的派生）。 */
export function createTheme(overrides: ThemeOverrides = {}): HqTheme {
  return {
    ...defaultTheme,
    mode: overrides.mode ?? defaultTheme.mode,
    palette: { ...defaultPalette, ...overrides.palette }
  };
}
